import { Directive, effect, inject, Injectable, input, InputSignal, Signal, signal, WritableSignal } from '@angular/core';
import { MaybeResponsive } from '../models';

/**
 * Carries the column span of the surrounding page block down to the components rendered inside it.
 * Provided by `ImageBlockColumnsDirective` and read by page components, which need it as the second
 * factor of their image slot width: viewport × block share ÷ items per row.
 */
@Injectable()
export class ImageBlockColumnsContext {
  // Properties & Signals
  /** The block's column span out of 12; undefined until a directive sets it. */
  private readonly _columns: WritableSignal<MaybeResponsive<number> | undefined> = signal<MaybeResponsive<number> | undefined>(
    undefined
  );

  // Getters & Setters
  /** The block's column span, read-only for consumers. */
  readonly columns: Signal<MaybeResponsive<number> | undefined> = this._columns.asReadonly();

  // Methods
  /**
   * Sets the column span for every component inside the block.
   * @param columns The block's column span out of 12.
   */
  set(columns: MaybeResponsive<number> | undefined): void {
    this._columns.set(columns);
  }
}

/**
 * Declares the column span of a page block, so the components inside it can size their images against
 * their real share of the viewport instead of assuming the full width. Belongs on the block wrapper in
 * a page renderer; components read it through `StandardComponent.imageSizes`.
 *
 * @example
 * <div [gsImageBlockColumns]="block.columns">…</div>
 */
@Directive({
  selector: '[gsImageBlockColumns]',
  providers: [ImageBlockColumnsContext]
})
export class ImageBlockColumnsDirective {
  // Services
  /** The context this directive provides to the components inside its element. */
  private readonly context: ImageBlockColumnsContext = inject(ImageBlockColumnsContext);

  // Inputs & Models
  /** The block's column span out of 12. */
  gsImageBlockColumns: InputSignal<MaybeResponsive<number> | undefined> = input<MaybeResponsive<number> | undefined>(undefined);

  constructor() {
    // Keeps the provided context in sync with the bound value
    effect(() => this.context.set(this.gsImageBlockColumns()));
  }
}
